import { useEffect, useRef, useState } from "react";
import {
  ShieldCheck, Upload, Camera, FileText, Loader2, CheckCircle2, AlertCircle,
  XCircle, RefreshCw, Phone, MessageCircle, X, ChevronRight, Sparkles, Clock,
} from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

/**
 * Phase 6.4 — Business License AI Pre-Approval card.
 *
 * Shown after successful registration. Optional upload (skippable).
 * Live progress bar + clear AI result with reasoning + resolution actions.
 *
 * Props:
 *   slug            (string) — event slug for the API path
 *   registrationId  (number) — required to bind the upload
 *   primary         (string) — brand primary color
 *   accent          (string) — brand accent color
 */

const SUPPORT_PHONE = "+1 888-695-6068";
const POLL_FALLBACK_MS = 800;

const STEP_LABELS = {
  upload_received:        { vi: "Đã nhận tài liệu",            en: "Upload complete" },
  ocr_running:            { vi: "Đang đọc tài liệu",            en: "Reading document" },
  extracting_fields:      { vi: "Trích xuất thông tin",         en: "Extracting information" },
  matching_registration:  { vi: "So sánh với hồ sơ đăng ký",     en: "Comparing with registration" },
  checking_license:       { vi: "Kiểm tra hiệu lực giấy phép",   en: "Verifying license" },
  calculating_score:      { vi: "Tính điểm độ tin cậy",          en: "Final AI decision" },
  completed:              { vi: "Hoàn tất",                     en: "Done" },
};

const STEP_ORDER = [
  "upload_received", "ocr_running", "extracting_fields",
  "matching_registration", "checking_license", "calculating_score", "completed",
];


export default function LicenseVerificationCard({ slug, registrationId, primary = "#0E5B4F", accent = "#C9A14A", licenseRequirement = "optional_ai" }) {
  const { lang } = useI18n();
  const L = (vi, en) => (lang === "en" ? en : vi);

  const [phase, setPhase] = useState("idle"); // idle | uploading | processing | done | error
  const [uploadError, setUploadError] = useState("");
  const [verification, setVerification] = useState(null); // last fetched verification obj
  const [skipped, setSkipped] = useState(false);
  const [showCallback, setShowCallback] = useState(false);

  const isRequired = licenseRequirement === "required_ai" || licenseRequirement === "required_manual";
  const isManualOnly = licenseRequirement === "required_manual";

  const pollTimer = useRef(null);

  // Stop polling on unmount
  useEffect(() => () => { if (pollTimer.current) clearTimeout(pollTimer.current); }, []);

  const pollStatus = async () => {
    try {
      const r = await api.get(`/public/events/${slug}/registrations/${registrationId}/license-status`);
      const v = r.data?.verification;
      if (!v) return;
      setVerification(v);
      if (v.ai_status === "processing") {
        const next = r.data?.polling_interval_ms || POLL_FALLBACK_MS;
        pollTimer.current = setTimeout(pollStatus, next);
      } else {
        setPhase("done");
      }
    } catch (e) {
      setPhase("error");
      setUploadError(L("Mất kết nối — vui lòng thử lại.", "Connection lost — try again."));
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploadError("");

    // Client-side guard rails
    const okTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!okTypes.includes(file.type)) {
      setUploadError(L("Chỉ chấp nhận JPG, PNG hoặc PDF.", "Only JPG, PNG, or PDF accepted."));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError(L("Tệp vượt quá 10MB.", "File exceeds 10MB."));
      return;
    }

    setPhase("uploading");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(
        `/public/events/${slug}/registrations/${registrationId}/license-upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setVerification(r.data?.verification);
      setPhase("processing");
      // Start polling
      pollTimer.current = setTimeout(pollStatus, r.data?.polling_interval_ms || POLL_FALLBACK_MS);
    } catch (e) {
      setPhase("error");
      setUploadError(e?.response?.data?.detail || L("Không tải lên được. Vui lòng thử lại.", "Upload failed. Try again."));
    }
  };

  const handleReset = () => {
    if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
    setVerification(null);
    setPhase("idle");
    setUploadError("");
  };

  if (skipped) return null;

  return (
    <div className="bg-white rounded-3xl p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.4)] border border-warmgray-200 relative overflow-hidden" data-testid="license-card">
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${primary}, ${accent}, ${primary})` }} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald2/10 text-emerald2">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold">
              {isRequired ? L("Bắt buộc", "Required") : L("Tuỳ chọn", "Optional")}
            </span>
          </div>
          <h3 className="mt-2 font-serif text-2xl md:text-3xl text-charcoal leading-tight">
            {L("Hồ sơ chủ tiệm", "Owner document")}
          </h3>
          <p className="mt-2 text-warmgray-700 text-sm leading-relaxed">
            {L(
              "Sự kiện này dành cho chủ tiệm. Anh/chị tải lên giấy tờ có tên mình là chủ tiệm nhé. Ví dụ: giấy chứng nhận thuế kinh doanh của thành phố, giấy phép tiệm của tiểu bang, giấy đăng ký tên kinh doanh (DBA), hoặc thư EIN.",
              "This event is for salon owners. Please upload a document showing your name as the owner. Examples: city business tax certificate, state salon license, DBA filing, or EIN letter.",
            )}
          </p>
          <p className="mt-2 text-warmgray-700 text-sm leading-relaxed">
            {L(
              "⚠️ Giấy phép thợ nail cá nhân không dùng được cho phần này.",
              "⚠️ A personal nail technician license doesn't work for this.",
            )}
          </p>
          <p className="mt-2 text-warmgray-500 text-xs leading-relaxed">
            {L(
              "Chụp đủ 4 góc, tránh bị chói đèn.",
              "Get all 4 corners in frame, avoid glare.",
            )}
          </p>
        </div>
        {phase === "idle" && !isRequired && (
          <button
            onClick={() => setSkipped(true)}
            className="text-xs text-warmgray-500 hover:text-warmgray-800 underline"
            data-testid="license-skip"
          >
            {L("Bỏ qua", "Skip")}
          </button>
        )}
      </div>

      {/* IDLE — show upload buttons */}
      {phase === "idle" && (
        <UploadStage onFile={handleFile} primary={primary} error={uploadError} lang={lang} L={L} />
      )}

      {/* UPLOADING — instant micro-state */}
      {phase === "uploading" && (
        <div className="mt-6 flex items-center gap-3 text-sm text-charcoal" data-testid="license-uploading">
          <Loader2 className="w-5 h-5 animate-spin text-emerald2" />
          {L("Đang tải tệp lên…", "Uploading…")}
        </div>
      )}

      {/* PROCESSING — live progress */}
      {phase === "processing" && verification && (
        <ProgressPanel verification={verification} lang={lang} L={L} primary={primary} />
      )}

      {/* DONE — result + resolution */}
      {phase === "done" && verification && (
        <ResultPanel
          verification={verification}
          slug={slug}
          registrationId={registrationId}
          onTryAgain={handleReset}
          onRequestCallback={() => setShowCallback(true)}
          primary={primary}
          lang={lang}
          L={L}
        />
      )}

      {/* ERROR — full reset */}
      {phase === "error" && (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-start gap-3" data-testid="license-error">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">{L("Có lỗi xảy ra", "Something went wrong")}</div>
            <div className="mt-1">{uploadError || L("Vui lòng thử lại.", "Please try again.")}</div>
            <button onClick={handleReset} className="mt-3 inline-flex items-center gap-1 text-emerald2 hover:underline">
              <RefreshCw className="w-4 h-4" /> {L("Thử lại", "Try again")}
            </button>
          </div>
        </div>
      )}

      {/* Callback modal */}
      {showCallback && (
        <CallbackModal
          slug={slug}
          registrationId={registrationId}
          verification={verification}
          onClose={() => setShowCallback(false)}
          primary={primary}
          lang={lang}
          L={L}
        />
      )}
    </div>
  );
}


// --------------------------------------------------------------------------- //
// STAGES
// --------------------------------------------------------------------------- //

function UploadStage({ onFile, primary, error, lang, L }) {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  return (
    <div className="mt-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="license-pick-file"
          className="group rounded-2xl border-2 border-dashed border-warmgray-300 hover:border-emerald2 px-4 py-6 flex flex-col items-center justify-center gap-2 transition-colors"
        >
          <Upload className="w-7 h-7 text-emerald2" />
          <div className="text-sm font-semibold text-charcoal">
            {L("Chọn tệp", "Choose file")}
          </div>
          <div className="text-[11px] text-warmgray-500">
            {L("JPG, PNG hoặc PDF · tối đa 10MB", "JPG, PNG, or PDF · max 10MB")}
          </div>
        </button>
        <button
          type="button"
          onClick={() => camRef.current?.click()}
          data-testid="license-take-photo"
          className="group rounded-2xl border-2 border-dashed border-warmgray-300 hover:border-emerald2 px-4 py-6 flex flex-col items-center justify-center gap-2 transition-colors"
        >
          <Camera className="w-7 h-7 text-emerald2" />
          <div className="text-sm font-semibold text-charcoal">
            {L("Chụp ảnh giấy phép", "Take a photo")}
          </div>
          <div className="text-[11px] text-warmgray-500">
            {L("Dùng camera trên điện thoại", "Uses your camera (mobile)")}
          </div>
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,application/pdf"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid="license-file-input"
      />
      <input
        ref={camRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid="license-camera-input"
      />
      {error && (
        <div className="mt-3 text-sm rounded-xl border border-[#7A2E2E]/20 bg-[#F5DADA] text-[#7A2E2E] px-4 py-3" data-testid="license-validation-error">
          {error}
        </div>
      )}
    </div>
  );
}


function ProgressPanel({ verification, lang, L, primary }) {
  const currentIdx = Math.max(0, STEP_ORDER.indexOf(verification.progress_state));
  const percent = verification.progress_percent || 0;
  return (
    <div className="mt-6" data-testid="license-progress">
      {/* Animated bar */}
      <div className="h-2 w-full bg-warmgray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${percent}%`, background: `linear-gradient(90deg, ${primary}, #C9A14A)` }}
          data-testid="license-progress-bar"
        />
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-warmgray-700 font-medium" data-testid="license-progress-label">
          {verification.status_message || STEP_LABELS[verification.progress_state]?.[lang === "en" ? "en" : "vi"]}
        </span>
        <span className="font-mono text-warmgray-500">{percent}%</span>
      </div>

      {/* Step checklist */}
      <ul className="mt-5 space-y-2.5">
        {STEP_ORDER.slice(0, -1).map((step, idx) => {
          const done = idx < currentIdx;
          const active = idx === currentIdx;
          return (
            <li key={step} className="flex items-center gap-3 text-sm" data-testid={`license-step-${step}`}>
              <span className="w-5 h-5 inline-flex items-center justify-center rounded-full shrink-0"
                style={{ background: done ? "#10b981" : active ? `${primary}22` : "#f1f1f0" }}>
                {done ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                ) : active ? (
                  <Loader2 className="w-3 h-3 text-emerald2 animate-spin" />
                ) : (
                  <span className="w-1.5 h-1.5 bg-warmgray-300 rounded-full" />
                )}
              </span>
              <span className={done ? "text-charcoal" : active ? "text-charcoal font-medium" : "text-warmgray-500"}>
                {STEP_LABELS[step]?.[lang === "en" ? "en" : "vi"]}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-5 text-xs text-warmgray-500 flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-emerald2" />
        {L("Quá trình này thường mất 5-10 giây.", "This usually takes 5–10 seconds.")}
      </div>
    </div>
  );
}


function ResultPanel({ verification, onTryAgain, onRequestCallback, primary, lang, L }) {
  const status = verification.ai_status;
  const score = verification.match_score ?? 0;
  const remaining = verification.attempts_remaining;
  const attempt = verification.attempt_count;
  const cap = verification.max_attempts;
  const noMoreAttempts = remaining != null && remaining <= 0;

  const AttemptsRemainingNote = ({ kind = "amber" }) => (
    (attempt && cap) ? (
      <div
        data-testid="license-attempts-remaining"
        className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
          kind === "red"
            ? "bg-red-50 border-red-200 text-red-800"
            : "bg-amber-50 border-amber-200 text-amber-900"
        }`}
      >
        {noMoreAttempts
          ? L(
              `Bạn đã dùng hết ${cap}/${cap} lần thử AI. Đội ngũ Admin sẽ liên hệ với bạn trong vòng 24 giờ.`,
              `You have used all ${cap}/${cap} AI attempts. Our Admin team will contact you within 24 hours.`,
            )
          : L(
              `Lần thử ${attempt}/${cap} — bạn còn ${remaining} lần thử lại.`,
              `Attempt ${attempt}/${cap} — you have ${remaining} attempt(s) left.`,
            )}
      </div>
    ) : null
  );

  // 🟢 Received — pending review (identical UX to needs_review per §M2)
  if (status === "ai_pre_approved") {
    return (
      <div className="mt-6" data-testid="license-result-approved">
        <ResultHeader
          icon={<Clock className="w-7 h-7 text-warmgray-600" />}
          ringColor="ring-warmgray-100"
          bg="bg-warmgray-50"
          tone="text-warmgray-800"
          eyebrow={L("ĐÃ NHẬN HỒ SƠ", "DOCUMENT RECEIVED")}
          title={L("Đã nhận hồ sơ — đang chờ duyệt", "Document received — pending review")}
          score={null}
        />
        <p className="mt-4 text-sm text-charcoal leading-relaxed" data-testid="license-result-message">
          {L(
            "Giấy tờ của bạn đã được gửi và sẽ được ban quản trị kiểm tra. Chúng tôi sẽ báo cho bạn khi có kết quả.",
            "Your document has been submitted and will be reviewed by our team. We'll let you know when it's been checked.",
          )}
        </p>
      </div>
    );
  }

  // 🟡 Needs Review (also fired for admin-flagged)
  if (status === "ai_needs_review" || verification.admin_status === "admin_flagged") {
    return (
      <div className="mt-6" data-testid="license-result-needs-review">
        <ResultHeader
          icon={<Clock className="w-7 h-7 text-warmgray-600" />}
          ringColor="ring-warmgray-100"
          bg="bg-warmgray-50"
          tone="text-warmgray-800"
          eyebrow={L("ĐÃ NHẬN HỒ SƠ", "DOCUMENT RECEIVED")}
          title={L("Đã nhận hồ sơ — đang chờ duyệt", "Document received — pending review")}
          score={null}
        />
        <p className="mt-4 text-sm text-charcoal leading-relaxed" data-testid="license-result-message">
          {L(
            "Giấy tờ của bạn đã được gửi và sẽ được ban quản trị kiểm tra. Chúng tôi sẽ báo cho bạn khi có kết quả.",
            "Your document has been submitted and will be reviewed by our team. We'll let you know when it's been checked.",
          )}
        </p>
      </div>
    );
  }

  // 🔴 Admin Rejected
  if (verification.admin_status === "admin_rejected") {
    return (
      <div className="mt-6" data-testid="license-result-admin-rejected">
        <ResultHeader
          icon={<XCircle className="w-7 h-7 text-red-600" />}
          ringColor="ring-red-100"
          bg="bg-red-50"
          tone="text-red-800"
          eyebrow={L("ĐỘI NGŨ TỪ CHỐI", "ADMIN REJECTED")}
          title={L("Đội ngũ chúng tôi đã xem và không thể duyệt giấy phép này.", "Our team reviewed your license and could not approve it.")}
          score={null}
        />
        {verification.admin_notes && (
          <div className="mt-4 rounded-xl bg-red-50/50 border border-red-200 px-4 py-3 text-sm text-red-900" data-testid="license-admin-note">
            <div className="font-semibold mb-1">{L("Ghi chú của đội ngũ", "Staff note")}:</div>
            {verification.admin_notes}
          </div>
        )}
        <ResolutionTips lang={lang} L={L} rejected />
        <ResolutionActions onTryAgain={onTryAgain} onRequestCallback={onRequestCallback} primary={primary} lang={lang} L={L} />
      </div>
    );
  }

  // 🔴 AI Failed — real user error only (bad file / wrong type / PDF > 5 pages).
  //     Provider outages route to admin queue instead — user never sees this.
  return (
    <div className="mt-6" data-testid="license-result-failed">
      <ResultHeader
        icon={<AlertCircle className="w-7 h-7 text-amber-600" />}
        ringColor="ring-amber-100"
        bg="bg-amber-50"
        tone="text-amber-800"
        eyebrow={L("HÌNH BỊ LỖI", "PROBLEM WITH FILE")}
        title={L("Hình bị lỗi — bạn thử chụp lại giúp mình nhé", "There was a problem with this file — please try again")}
        score={null}
      />
      <p className="mt-4 text-sm text-charcoal leading-relaxed" data-testid="license-result-message">
        {L(
          "Chụp đủ 4 góc, tránh bị chói đèn. Chấp nhận JPG, PNG hoặc PDF 1 trang.",
          "Get all 4 corners in frame, avoid glare. JPG, PNG, or single-page PDF.",
        )}
      </p>
      <AttemptsRemainingNote kind={noMoreAttempts ? "red" : "amber"} />
      <ResolutionActions onTryAgain={onTryAgain} onRequestCallback={onRequestCallback} primary={primary} lang={lang} L={L} disabled={noMoreAttempts} />
    </div>
  );
}


function ResultHeader({ icon, ringColor, bg, tone, eyebrow, title, score }) {
  return (
    <div className={`rounded-2xl ${bg} px-5 py-5 ring-4 ${ringColor} flex items-center gap-4`}>
      <div className="bg-white rounded-full p-2 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${tone}`} data-testid="license-result-eyebrow">{eyebrow}</div>
        <div className="mt-1 font-serif text-lg md:text-xl text-charcoal leading-tight" data-testid="license-result-title">{title}</div>
      </div>
      {typeof score === "number" && (
        <div className="text-right shrink-0" data-testid="license-result-score">
          <div className="font-mono text-3xl font-bold text-charcoal">{score}<span className="text-base text-warmgray-500">%</span></div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-warmgray-500">Confidence</div>
        </div>
      )}
    </div>
  );
}


function FieldBreakdown({ verification, lang, L }) {
  const fields = [
    ...(verification.matched_fields || []).map((f) => ({ ...f, ok: true })),
    ...(verification.mismatched_fields || []).map((f) => ({ ...f, ok: false })),
  ];
  if (!fields.length) return null;
  const VI = {
    business_name: "Tên doanh nghiệp", owner_name: "Tên chủ", address: "Địa chỉ",
    document_quality: "Chất lượng hình", license_validity: "Hiệu lực giấy phép",
    license_number: "Số giấy phép", expiration_date: "Ngày hết hạn",
  };
  const EN = {
    business_name: "Business Name", owner_name: "Owner Name", address: "Address",
    document_quality: "Image Quality", license_validity: "License Validity",
    license_number: "License Number", expiration_date: "Expiration",
  };
  return (
    <ul className="mt-5 grid sm:grid-cols-2 gap-2" data-testid="license-field-breakdown">
      {fields.map((f, i) => (
        <li key={`${f.field}-${i}`} className="flex items-center gap-2 text-sm">
          {f.ok ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
          )}
          <span className="text-charcoal">
            {(lang === "en" ? EN[f.field] : VI[f.field]) || f.field}
          </span>
          {typeof f.score === "number" && (
            <span className="ml-auto text-xs text-warmgray-500 font-mono">{f.score}</span>
          )}
        </li>
      ))}
    </ul>
  );
}


function ResolutionTips({ lang, L, rejected }) {
  return (
    <ul className="mt-4 grid gap-1 text-xs text-warmgray-700">
      {rejected ? (
        <>
          <li>• {L("Tải lên giấy phép đã cập nhật", "Upload a corrected document")}</li>
          <li>• {L("Liên hệ đội ngũ để xác minh", "Contact support to verify")}</li>
          <li>• {L("Yêu cầu gọi lại bên dưới", "Request a callback below")}</li>
        </>
      ) : (
        <>
          <li>• {L("Tải lên hình rõ hơn", "Upload a clearer license")}</li>
          <li>• {L("Đảm bảo tên doanh nghiệp + tên chủ khớp với đăng ký", "Make sure business name + owner name match your registration")}</li>
          <li>• {L("Liên hệ đội ngũ nếu tên trên giấy phép khác với tên kinh doanh thực tế", "Contact support if your legal name/business name differs")}</li>
        </>
      )}
    </ul>
  );
}


function ResolutionActions({ onTryAgain, onRequestCallback, primary, lang, L, disabled = false }) {
  return (
    <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="license-resolution-actions">
      <button
        onClick={onTryAgain}
        disabled={disabled}
        data-testid="license-upload-another"
        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium ${
          disabled
            ? "bg-warmgray-200 text-warmgray-500 cursor-not-allowed"
            : "bg-charcoal text-white hover:bg-charcoal-900"
        }`}
      >
        <RefreshCw className="w-4 h-4" />
        {disabled
          ? L("Đã hết lần thử", "No retries left")
          : L("Tải lên tài liệu khác", "Upload another document")}
      </button>
      <a
        href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}
        data-testid="license-call-support"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-warmgray-300 text-charcoal text-sm font-medium hover:bg-warmgray-50"
      >
        <Phone className="w-4 h-4 text-emerald2" /> {SUPPORT_PHONE}
      </a>
      <button
        onClick={onRequestCallback}
        data-testid="license-request-callback"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald2/10 border border-emerald2/30 text-emerald2 text-sm font-medium hover:bg-emerald2/15"
      >
        <MessageCircle className="w-4 h-4" /> {L("Yêu cầu gọi lại", "Request a callback")}
      </button>
    </div>
  );
}


function CallbackModal({ slug, registrationId, verification, onClose, primary, lang, L }) {
  const [form, setForm] = useState({ name: "", phone: "", best_time: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.phone.trim()) {
      setErr(L("Vui lòng nhập số điện thoại.", "Phone is required."));
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      await api.post(`/public/events/${slug}/registrations/${registrationId}/support/callback`, form);
      setSuccess(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || L("Không gửi được — vui lòng thử lại.", "Could not submit — try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="license-callback-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-warmgray-500 hover:text-charcoal" data-testid="license-callback-close">
          <X className="w-5 h-5" />
        </button>
        {success ? (
          <div className="py-6 text-center" data-testid="license-callback-success">
            <div className="w-14 h-14 mx-auto bg-emerald-50 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="mt-4 font-serif text-2xl text-charcoal">
              {L("Cảm ơn bạn!", "Thank you!")}
            </h3>
            <p className="mt-2 text-sm text-warmgray-700">
              {L(
                "Đội ngũ sẽ gọi lại để xác minh đăng ký của bạn.",
                "Our team will call you back to verify your registration.",
              )}
            </p>
            <button onClick={onClose} className="mt-5 px-5 py-2 rounded-full bg-charcoal text-white text-sm">
              {L("Đóng", "Close")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <h3 className="font-serif text-2xl text-charcoal">
              {L("Yêu cầu gọi lại", "Request a callback")}
            </h3>
            <p className="text-xs text-warmgray-600">
              {L("Để lại thông tin, đội ngũ sẽ liên hệ trong giờ làm việc.", "Leave your info — our team will reach out during business hours.")}
            </p>
            <CallbackField label={L("Họ và tên", "Name")}     value={form.name}      onChange={(v) => setForm({ ...form, name: v })}      testId="callback-name" />
            <CallbackField label={L("Số điện thoại *", "Phone number *")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} type="tel" testId="callback-phone" />
            <CallbackField label={L("Thời gian thuận tiện", "Best time to call")} value={form.best_time} onChange={(v) => setForm({ ...form, best_time: v })} placeholder={L("VD: Sáng 9-11h", "e.g. mornings")} testId="callback-time" />
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-warmgray-700">
                {L("Lời nhắn", "Message")}
              </span>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                data-testid="callback-message"
                className="mt-1 w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40 focus:border-emerald2"
              />
            </label>
            {err && (
              <div className="text-sm rounded-xl border border-red-200 bg-red-50 text-red-800 px-3 py-2">{err}</div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="callback-submit"
              className="w-full px-5 py-3 rounded-full bg-emerald2 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              {L("Gửi yêu cầu", "Submit request")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}


function CallbackField({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-warmgray-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-1 w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40 focus:border-emerald2"
      />
    </label>
  );
}
